/**
 * Bridge Result Helpers (canonical, core-level)
 *
 * MVC Location: core/bridge/bridgeResult.ts
 *
 * Helpers for working with bridge call_function response envelopes.
 * The bridge wraps UFUNCTION return values as { returnValue: ... }.
 * Any code (core shell or module) that calls callFunction must unwrap the
 * envelope before type-checking / Zod parsing.
 *
 * This is the single source of truth. The CC module's
 * controllers/utils/bridgeUtils.ts re-exports these so existing CC import
 * sites keep working unchanged without violating the module-boundary rule
 * (core must not import from a module).
 */

// ============================================================================
// parseFStringResult
// ============================================================================

/**
 * Parse an FString JSON return value from a C++ UFUNCTION.
 *
 * C++ UFUNCTIONs that return FString serialize their results as JSON strings.
 * After unwrapBridgeResult, the value is still a JSON string that needs
 * JSON.parse before Zod schema validation.
 *
 * - If data is a string, tries JSON.parse and returns the parsed value
 * - If data is already an object/array/null/undefined, returns as-is
 * - If JSON.parse fails, throws Error with descriptive message
 */
export function parseFStringResult(data: unknown): unknown {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data)
    } catch {
      throw new Error(`Failed to parse FString JSON: ${data.slice(0, 100)}`)
    }
  }
  return data
}

// ============================================================================
// unwrapBridgeResult
// ============================================================================

/**
 * Unwrap the bridge call_function response envelope.
 * Bridge wraps UFUNCTION return values as { returnValue: ... }.
 *
 * If the result contains a `returnValue` key, returns that inner value.
 * If the inner value is a JSON string, parses it automatically.
 * Otherwise returns the result as-is (already unwrapped or void response).
 */
export function unwrapBridgeResult(result: unknown): unknown {
  let value = result
  if (value && typeof value === 'object' && 'returnValue' in value) {
    value = (value as { returnValue: unknown }).returnValue
  }
  // Auto-parse JSON strings — PropertyMarshaller may serialize structs/arrays as JSON strings
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch {
      return value // Not JSON — return as plain string
    }
  }
  return value
}
